import Button from '@cobliteam/ui/components/Button';
import Typography from '@cobliteam/ui/components/Typography';
import Icon, { type IconNames } from '@cobliteam/ui/icons';
import { cn } from '@cobliteam/ui/utils';
import ShineBorder from '@cobliteam/ui-business/components/border/ShineBorder';
import type { ReactNode } from 'react';

/** Tema visual do card por tipo: classes da moldura + paleta da borda animada + ícone/título. */
export interface NotificationCardTheme {
  containerClassName: string;
  shineColors: string[];
  shineBorderWidth?: number;
  shineDuration?: number;
  iconWrapClassName: string;
  titleClassName: string;
}

interface NotificationCardShellProps {
  theme: NotificationCardTheme;
  iconName: IconNames;
  /** Badge de urgência/tipo no header (slot — o tipo monta seu próprio Tag). */
  badge: ReactNode;
  /** Título já renderizado (ex.: com markup `<bold>` resolvido). */
  title: ReactNode;
  /** Descrição já renderizada (nó completo, com suas próprias classes). */
  description: ReactNode;
  /** Mídia opcional (ex.: preview de footage) renderada entre a descrição e a linha de local. */
  media?: ReactNode;
  /** Linha de local opcional (nó de texto). */
  location?: ReactNode;
  /** Timestamp relativo opcional (ex.: "há 5 minutos") no header, à direita. */
  timestamp?: ReactNode;
  dismissLabel: string;
  onDismiss: () => void;
  /** CTA principal (ex.: AIButton do risco) com o onAction já ligado pelo tipo. */
  action: ReactNode;
  className?: string;
}

/**
 * Caixa visual compartilhada do card de notificação (ícone + badge + fechar, título, descrição, local,
 * ações). Reusada entre o card flutuante (qualquer surface de card) e o toast (sonner) para terem o MESMO
 * layout. Cores/ícone/cópia vêm do tipo via `theme` + slots, então cada tipo customiza mantendo a estrutura.
 */
const NotificationCardShell = ({
  theme,
  iconName,
  badge,
  title,
  description,
  media,
  location,
  timestamp,
  dismissLabel,
  onDismiss,
  action,
  className,
}: NotificationCardShellProps) => (
  <div className={cn('relative flex w-full flex-col gap-[9px] rounded-2xl border-solid p-[14px]', theme.containerClassName, className)}>
    <ShineBorder shineColor={theme.shineColors} borderWidth={theme.shineBorderWidth ?? 2} duration={theme.shineDuration ?? 6} />

    <div className='flex w-full items-center justify-between'>
      <div className='flex items-center gap-2'>
        <span className={cn('flex size-9 shrink-0 items-center justify-center rounded-full', theme.iconWrapClassName)} aria-hidden>
          <Icon name={iconName} size='sm' />
        </span>
        {badge}
      </div>
      <button
        type='button'
        onClick={onDismiss}
        aria-label={dismissLabel}
        className='flex size-6 cursor-pointer items-center justify-center rounded-lg text-[#737373] transition-colors hover:bg-neutral-100'
      >
        <Icon name='x' size='sm' />
      </button>
    </div>

    {/* Mídia (ex.: thumbnail de footage) à esquerda; título + descrição à direita, lado a lado. Sem mídia,
        o slot colapsa e o texto ocupa a largura toda. */}
    <Typography variant='description-title' className={cn('break-words leading-[1.35] [overflow-wrap:anywhere]', theme.titleClassName)}>
      {title}
    </Typography>
    <div className='flex items-start gap-3'>
      {media}
      <div className='flex min-w-0 flex-1 flex-col gap-[9px]'>{description}</div>
    </div>

    {location || timestamp ? (
      <div className='flex items-start gap-2 text-gray-8'>
        {timestamp ? (
          <div className='flex shrink-0 items-start gap-1 self-start'>
            <Icon name='clock' size='xs' className='mt-0.5 shrink-0' aria-hidden />
            <Typography variant='caption-xs' className='whitespace-nowrap tabular-nums leading-[1.4]'>
              {timestamp}
            </Typography>
          </div>
        ) : null}
        {location ? (
          <div className='flex min-w-0 items-start gap-1.5'>
            <Icon name='map-pin' size='xs' className='mt-0.5 shrink-0' aria-hidden />
            <Typography variant='caption-xs' className='break-words leading-[1.4] [overflow-wrap:anywhere]'>
              {location}
            </Typography>
          </div>
        ) : null}
      </div>
    ) : null}

    <div className='flex items-center justify-between gap-3 pt-1'>
      <Button variant='ghost' color='default' size='sm' onClick={onDismiss}>
        {dismissLabel}
      </Button>
      {action}
    </div>
  </div>
);

export default NotificationCardShell;
